import { Col, Container, Row, Surface, Txt } from '@vst/beam';
import styles from './checkoutCartDueToday.module.scss';
import { selectCartDomain, selectOnetimeDiscount, selectOnetimeTotalWithoutPromo } from '@buy-viasat/redux/src/cart';
import { useSelector } from 'react-redux';
import { calculateDueTodayTotal, isDefined, priceDecimalFormat } from '@buy-viasat/utils';
import { CustomerType, selectCurrencySymbol, selectCustomerType, selectLocale } from '@buy-viasat/redux/src/app';
import { useTotalPriceVisibility } from '../hooks';
import { UpfrontFees } from './UpfrontFees';

type CheckoutCartDueTodayProps = {
  dueToday: string;
  totalDueToday: string;
  calculatedLaterLabel: string;
  isBackgroundAPICallLoading: boolean;
  freeDiscountTranslationsKey: string;
};

export const CheckoutCartDueToday = ({
  dueToday,
  totalDueToday,
  calculatedLaterLabel,
  isBackgroundAPICallLoading,
  freeDiscountTranslationsKey,
}: CheckoutCartDueTodayProps) => {
  const cart = useSelector(selectCartDomain);
  const onetimeTotalWithoutPromo = useSelector(selectOnetimeTotalWithoutPromo);
  const oneTimeDiscount = useSelector(selectOnetimeDiscount);
  const locale = useSelector(selectLocale);
  const customerType = useSelector(selectCustomerType);
  const currencySymbol = useSelector(selectCurrencySymbol);
  const { visible: priceValueDisplayed } = useTotalPriceVisibility(isBackgroundAPICallLoading);

  const { feesAndDiscounts } = cart;
  const isBusinessUser = customerType === CustomerType.B;

  const { dueTodayTotal, dueTodayTotalValue } = calculateDueTodayTotal(
    onetimeTotalWithoutPromo || 0,
    oneTimeDiscount || 0,
    locale,
    currencySymbol,
  );

  return (
    <Container fluidUntil={'md'} pb="24px" data-testid="dueTodayContainer">
      <Row edge2edge>
        <Col>
          <Surface pt="24px" className={styles['container']} data-testid="dueTodaySurface">
            {priceValueDisplayed && (
              <>
                <Txt
                  variant="headerCapsMedium"
                  pb="12px"
                  color="subtle"
                  className={styles['dueToday']}
                  data-testid="dueToday"
                >
                  {dueToday}
                </Txt>
                {feesAndDiscounts && (
                  <UpfrontFees
                    feesAndDiscounts={feesAndDiscounts}
                    locale={locale}
                    currencySymbol={currencySymbol}
                    freeLabel={freeDiscountTranslationsKey}
                  />
                )}
              </>
            )}
            <Surface
              className={priceValueDisplayed ? styles['dueTodayTotal'] : styles['dueTodayTotalNoBorderTop']}
              data-testid="dueTodayTotal"
              pt="12px"
              pb="32px"
            >
              <Surface className={styles['dueTodayHeadline']}>
                <Txt variant="heading6">{totalDueToday}</Txt>
                <Txt
                  variant={priceValueDisplayed ? 'paragraphBold' : 'bodySmallItalic'}
                  className={styles[priceValueDisplayed ? 'dueTodayValue' : 'priceCalculatedLater']}
                >
                  {isDefined(dueTodayTotal) && priceValueDisplayed
                    ? dueTodayTotalValue
                    : !isBusinessUser
                      ? calculatedLaterLabel
                      : '--'}
                </Txt>
              </Surface>
            </Surface>
          </Surface>
        </Col>
      </Row>
    </Container>
  );
};
